import { Check, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { events as eventsApi, type Attendance } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState } from '../../components/ui/Bits';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { PersonRow } from './PersonRow';
import { t } from '../../i18n';

/**
 * Les participants d'un evenement, page a part.
 *
 * Ils vivaient au bas de l'ecran de gestion, sous les demandes et la
 * feuille de presence : le jour de l'evenement, pointer quelqu'un
 * demandait de traverser tout le reste. La page s'ouvre depuis sa tuile
 * et grandit a sa place — c'est de la qu'elle sort.
 *
 * Elle relit l'evenement pour elle-meme plutot que de le recevoir en
 * chemin : une adresse doit pouvoir s'ouvrir seule, au retour d'un
 * profil comme au rappel d'une notification.
 */
export function EventParticipantsScreen() {
  const { eventId = '' } = useParams();
  const { user } = useSession();
  const { data, loading, error, reload, setData } = useAsync(
    () => eventsApi.get(eventId),
    [eventId],
    `event:${eventId}`,
  );

  const [presences, setPresences] = useState<Attendance>({});
  const [busy, setBusy] = useState<string | null>(null);
  /* Une feuille arretee ne se recoche plus : les absences sont deja
     inscrites au casier des interesses. */
  const [feuilleArretee, setFeuilleArretee] = useState<number | null>(null);
  const [recherche, setRecherche] = useState('');
  const { pending, request, cancel, confirm } = useConfirm();

  useEffect(() => {
    if (!eventId) return;
    let vivant = true;
    void eventsApi
      .attendance(eventId)
      .then((valeur) => vivant && setPresences(valeur))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    let vivant = true;
    void eventsApi
      .sheetClosedAt(eventId)
      .then((quand) => vivant && setFeuilleArretee(quand))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [eventId]);

  const isOrganizer = Boolean(data && user && data.organizer.id === user.id);

  /* Sans accent ni majuscule : « chaima » doit trouver « Chaïma.H ». */
  const aplati = (texte: string) =>
    texte
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  const vus = (data?.participants ?? []).filter((membre) =>
    aplati(membre.username).includes(aplati(recherche.trim())),
  );

  async function togglePresence(memberId: string) {
    if (!data) return;
    setBusy(memberId);
    try {
      setPresences(
        presences[memberId]
          ? await eventsApi.clearPresent(data, memberId)
          : await eventsApi.markPresent(data, memberId, 'manuel'),
      );
    } finally {
      setBusy(null);
    }
  }

  async function retirer(memberId: string) {
    if (!data) return;
    setBusy(memberId);
    try {
      await eventsApi.remove(data, memberId);
      setData((precedent) =>
        precedent
          ? {
              ...precedent,
              participants: precedent.participants.filter((autre) => autre.id !== memberId),
            }
          : precedent,
      );
    } catch {
      reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <SubScreen
      entree="zoom"
      title={t.sorties.participants.titre}
      action={
        data && (
          <span className="feed__count">
            {data.participants.length} / {data.maxParticipants}
          </span>
        )
      }
      overlay={<ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />}
    >
      {loading && !data && <ScreenLoader />}
      {error && !data && <ScreenError message={error} onRetry={reload} />}

      {data && !isOrganizer && (
        <EmptyState
          title={t.sorties.reserve.titre}
          hint={t.sorties.participants.reserveAide}
        />
      )}

      {data && isOrganizer && (
        <>
          {/* Trente noms se parcourent des yeux ; cent se cherchent. */}
          <label className="ev-panneau__recherche">
            <MagnifyingGlass size={18} />
            <input
              type="search"
              value={recherche}
              placeholder={t.sorties.participants.chercher}
              aria-label={t.sorties.participants.chercher}
              onChange={(evenement) => setRecherche(evenement.target.value)}
            />
            {/* La croix du fil : elle vide le champ sans baisser le clavier. */}
            {recherche && (
              <button
                type="button"
                className="ev-panneau__effacer"
                aria-label={t.composants.champ.effacer}
                onMouseDown={(evenement) => evenement.preventDefault()}
                onClick={() => setRecherche('')}
              >
                <X size={15} weight="bold" />
              </button>
            )}
          </label>

          {vus.length === 0 ? (
            <p className="ev-manage__none">{t.sorties.participants.personne}</p>
          ) : (
            vus.map((member) => {
              const present = Boolean(presences[member.id]);
              return (
                <PersonRow
                  key={member.id}
                  member={member}
                  /* Rien a dire d'un inscrit ordinaire : sa presence dans
                     la liste le dit. Seul ce qui s'en ecarte se nomme. */
                  note={
                    present
                      ? t.sorties.participants.present
                      : member.id === data.organizer.id
                        ? t.sorties.participants.organise
                        : feuilleArretee
                          ? t.sorties.participants.absent
                          : undefined
                  }
                  /*
                    Retirer quelqu'un se fait en restant appuye, comme
                    dans les messageries : une croix a cote d'une case de
                    presence invitait a se tromper de cible, et l'une des
                    deux est irreversible.
                  */
                  onHold={
                    member.id === data.organizer.id || data.isPast
                      ? undefined
                      : () =>
                          request(t.sorties.participants.retirerQ(member.username), () =>
                            void retirer(member.id),
                          )
                  }
                >
                  {/* La presence se coche et se decoche : un scan errone se repare. */}
                  <button
                    className={`ev-person__act ev-person__act--here${
                      present ? ' ev-person__act--on' : ''
                    }`}
                    aria-label={
                      present
                        ? t.sorties.participants.retirerPresence(member.username)
                        : t.sorties.participants.estLa(member.username)
                    }
                    aria-pressed={present}
                    disabled={feuilleArretee !== null || busy !== null}
                    onClick={() => void togglePresence(member.id)}
                  >
                    <Check size={20} weight="bold" />
                  </button>
                </PersonRow>
              );
            })
          )}
        </>
      )}
    </SubScreen>
  );
}
